import { useState, useEffect } from 'react'
import {
  Calendar,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Flag,
  X,
  ExternalLink,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { Switch } from '@/shared/components/ui/switch'
import { Badge } from '@/shared/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Separator } from '@/shared/components/ui/separator'
import { Card, CardContent } from '@/components/ui/card'
import {
  useMarcadores,
  useCreateMarcador,
  useUpdateMarcador,
  useDeleteMarcador,
} from '@/modules/registros/hooks/useConfiguraciones'
import type { MarcadorEspecial } from '../types'

// Parsear fecha sin problemas de timezone
const parseDateSafe = (dateString: string): Date => {
  if (dateString.includes('T')) {
    return new Date(dateString)
  }
  return new Date(dateString + 'T12:00:00')
}

const TIPO_CHOICES = [
  { value: 'evento', label: 'Evento' },
  { value: 'lanzamiento', label: 'Lanzamiento' },
  { value: 'campana', label: 'Campaña' },
  { value: 'milestone', label: 'Hito' },
  { value: 'otro', label: 'Otro' },
]

const COLOR_CHOICES = [
  { value: '#f6c23e', label: 'Amarillo', bg: 'bg-yellow-400' },
  { value: '#e74a3b', label: 'Rojo', bg: 'bg-red-500' },
  { value: '#1cc88a', label: 'Verde', bg: 'bg-green-500' },
  { value: '#36b9cc', label: 'Azul', bg: 'bg-cyan-500' },
  { value: '#6f42c1', label: 'Púrpura', bg: 'bg-purple-600' },
  { value: '#fd7e14', label: 'Naranja', bg: 'bg-orange-500' },
]

interface MarcadoresDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  configId: number
  configTitulo: string
}

interface MarcadorFormData {
  fecha: string
  titulo: string
  descripcion: string
  url_reporte: string
  tipo: string
  color: string
  activo: boolean
  ultimo_hito: boolean
  primer_hito: boolean
}

const defaultFormData: MarcadorFormData = {
  fecha: '',
  titulo: '',
  descripcion: '',
  url_reporte: '',
  tipo: 'evento',
  color: '#f6c23e',
  activo: true,
  ultimo_hito: false,
  primer_hito: false,
}

export function MarcadoresDialog({
  open,
  onOpenChange,
  configId,
  configTitulo,
}: MarcadoresDialogProps) {
  const [showForm, setShowForm] = useState(false)
  const [editingMarcador, setEditingMarcador] = useState<MarcadorEspecial | null>(null)
  const [formData, setFormData] = useState<MarcadorFormData>(defaultFormData)

  const { data, isLoading, error } = useMarcadores(configId)
  const createMutation = useCreateMarcador()
  const updateMutation = useUpdateMarcador()
  const deleteMutation = useDeleteMarcador()

  const marcadores = data?.marcadores || []

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setShowForm(false)
      setEditingMarcador(null)
      setFormData(defaultFormData)
    }
  }, [open])

  const handleInputChange = (field: keyof MarcadorFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleEdit = (marcador: MarcadorEspecial) => {
    setEditingMarcador(marcador)
    setFormData({
      fecha: marcador.fecha,
      titulo: marcador.titulo,
      descripcion: marcador.descripcion || '',
      url_reporte: marcador.url_reporte || '',
      tipo: marcador.tipo,
      color: marcador.color,
      activo: marcador.activo,
      ultimo_hito: marcador.ultimo_hito,
      primer_hito: marcador.primer_hito,
    })
    setShowForm(true)
  }

  const handleCreate = () => {
    setEditingMarcador(null)
    setFormData(defaultFormData)
    setShowForm(true)
  }

  const handleCancelForm = () => {
    setShowForm(false)
    setEditingMarcador(null)
    setFormData(defaultFormData)
  }

  const handleDelete = async (marcador: MarcadorEspecial) => {
    if (window.confirm(`¿Estás seguro de eliminar el hito "${marcador.titulo}"?`)) {
      try {
        await deleteMutation.mutateAsync({ marcadorId: marcador.id, configId })
      } catch (error) {
        console.error('Error al eliminar:', error)
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (editingMarcador) {
        await updateMutation.mutateAsync({
          marcadorId: editingMarcador.id,
          configId,
          payload: formData,
        })
      } else {
        await createMutation.mutateAsync({
          configuracion_id: configId,
          ...formData,
        })
      }
      handleCancelForm()
    } catch (error) {
      console.error('Error al guardar:', error)
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  const sortedMarcadores = [...marcadores].sort((a, b) => {
    if (a.orden !== b.orden) return a.orden - b.orden
    return parseDateSafe(b.fecha).getTime() - parseDateSafe(a.fecha).getTime()
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Gestionar Hitos - {configTitulo}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Botón agregar */}
          {!showForm && (
            <Button onClick={handleCreate} size="sm" className="bg-teal-800 hover:bg-teal-900 text-white">
              <Plus className="mr-2 h-4 w-4" />
              Agregar Hito
            </Button>
          )}

          {/* Formulario */}
          {showForm && (
            <Card className="border-2 border-dashed">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-sm">
                    {editingMarcador ? 'Editar Hito' : 'Nuevo Hito'}
                  </h4>
                  <Button variant="ghost" size="sm" onClick={handleCancelForm}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fecha">Fecha *</Label>
                      <Input
                        id="fecha"
                        type="date"
                        value={formData.fecha}
                        onChange={(e) => handleInputChange('fecha', e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tipo">Tipo *</Label>
                      <Select
                        value={formData.tipo}
                        onValueChange={(value) => handleInputChange('tipo', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPO_CHOICES.map((tipo) => (
                            <SelectItem key={tipo.value} value={tipo.value}>
                              {tipo.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="titulo">Título *</Label>
                      <Input
                        id="titulo"
                        value={formData.titulo}
                        onChange={(e) => handleInputChange('titulo', e.target.value)}
                        placeholder="Ej: Lanzamiento de App"
                        maxLength={100}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="color">Color *</Label>
                      <Select
                        value={formData.color}
                        onValueChange={(value) => handleInputChange('color', value)}
                      >
                        <SelectTrigger>
                          <SelectValue>
                            <div className="flex items-center gap-2">
                              <span
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: formData.color }}
                              />
                              {COLOR_CHOICES.find((c) => c.value === formData.color)?.label}
                            </div>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {COLOR_CHOICES.map((color) => (
                            <SelectItem key={color.value} value={color.value}>
                              <div className="flex items-center gap-2">
                                <span
                                  className="w-4 h-4 rounded-full"
                                  style={{ backgroundColor: color.value }}
                                />
                                {color.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="descripcion">Descripción</Label>
                    <Textarea
                      id="descripcion"
                      value={formData.descripcion}
                      onChange={(e) => handleInputChange('descripcion', e.target.value)}
                      placeholder="Descripción detallada del evento (opcional)"
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="url_reporte">URL del Reporte</Label>
                    <Input
                      id="url_reporte"
                      type="url"
                      value={formData.url_reporte}
                      onChange={(e) => handleInputChange('url_reporte', e.target.value)}
                      placeholder="https://ejemplo.com/reporte (opcional)"
                    />
                  </div>

                  <div className="flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="activo"
                        checked={formData.activo}
                        onCheckedChange={(checked) => handleInputChange('activo', checked)}
                      />
                      <Label htmlFor="activo" className="text-sm">
                        Hito Activo
                      </Label>
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch
                        id="primer_hito"
                        checked={formData.primer_hito}
                        onCheckedChange={(checked) => handleInputChange('primer_hito', checked)}
                      />
                      <Label htmlFor="primer_hito" className="text-sm flex items-center gap-1">
                        <Flag className="h-3 w-3 text-green-600" />
                        Primer Hito
                      </Label>
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch
                        id="ultimo_hito"
                        checked={formData.ultimo_hito}
                        onCheckedChange={(checked) => handleInputChange('ultimo_hito', checked)}
                      />
                      <Label htmlFor="ultimo_hito" className="text-sm flex items-center gap-1">
                        <Flag className="h-3 w-3 text-yellow-600" />
                        Último Hito
                      </Label>
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>• <strong>Primer Hito:</strong> Define la fecha mínima (inicio) de las gráficas.</p>
                    <p>• <strong>Último Hito:</strong> Define la fecha máxima (fin) de las gráficas.</p>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={handleCancelForm}>
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={isPending}
                      className="bg-teal-800 hover:bg-teal-900 text-white"
                    >
                      {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {editingMarcador ? 'Guardar Cambios' : 'Crear Hito'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <Separator />

          {/* Lista de marcadores */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm text-muted-foreground">
              Hitos Existentes ({marcadores.length})
            </h4>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="text-center py-8 text-destructive">
                Error al cargar los hitos. Por favor, intenta de nuevo.
              </div>
            ) : marcadores.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="mx-auto h-12 w-12 mb-2 opacity-50" />
                <p>No hay hitos registrados para esta configuración.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sortedMarcadores.map((marcador) => (
                  <Card
                    key={marcador.id}
                    className={`${!marcador.activo ? 'opacity-50' : ''} ${
                      marcador.primer_hito ? 'ring-2 ring-green-400' : ''
                    } ${
                      marcador.ultimo_hito ? 'ring-2 ring-yellow-400' : ''
                    }`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: marcador.color }}
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{marcador.titulo}</span>
                              {marcador.primer_hito && (
                                <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                                  <Flag className="h-3 w-3 mr-1" />
                                  Primer Hito
                                </Badge>
                              )}
                              {marcador.ultimo_hito && (
                                <Badge variant="outline" className="text-xs bg-yellow-50">
                                  <Flag className="h-3 w-3 mr-1" />
                                  Último Hito
                                </Badge>
                              )}
                              {marcador.url_reporte && (
                                <a
                                  href={marcador.url_reporte}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>
                                {parseDateSafe(marcador.fecha).toLocaleDateString('es-ES', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </span>
                              <span>•</span>
                              <span className="capitalize">{marcador.tipo}</span>
                              {!marcador.activo && (
                                <>
                                  <span>•</span>
                                  <Badge variant="secondary" className="text-xs">
                                    Inactivo
                                  </Badge>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(marcador)}
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(marcador)}
                            disabled={deleteMutation.isPending}
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4 text-gray-600" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
